import { useState } from 'react'
import { BadgeCheck, Loader2, ShieldQuestion } from 'lucide-react'
import { DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { VoterVerificationEntity } from '@/shared/api/types'
import { addVerification, voterVerifications, VERIFICATION_LABEL } from './api'

/**
 * Recording a verification.
 *
 * The backend has had `POST /voters/{id}/verifications` since Phase 3 and no
 * screen has ever called it, so `verification_status` could only ever read
 * UNVERIFIED no matter how many IDs a coordinator actually sighted. This is that
 * screen.
 *
 * Two things the form has to make plain, because they are the parts people get
 * wrong:
 *
 *  - **The trail is append-only and the status is derived from the latest
 *    entry.** You are not editing a field; you are recording an observation, and
 *    the status follows from the method and the result.
 *  - **Inconclusive is not a failure.** It means the check did not settle the
 *    question. Recording one leaves an existing verification standing — an
 *    absence of evidence must not destroy evidence already gathered.
 */

const METHODS = [
  { code: 'ID_SIGHTED', label: 'ID sighted', hint: 'The physical national ID was seen' },
  { code: 'FIELD_VISIT', label: 'Field visit', hint: 'Confirmed in person at the residence' },
  { code: 'PHONE_OTP', label: 'Phone OTP', hint: 'A code sent to their number was returned' },
  { code: 'DOCUMENT', label: 'Document', hint: 'Another document evidenced identity' },
  { code: 'IEBC_PORTAL_MATCH', label: 'IEBC portal match', hint: 'Matched against the IEBC register' },
  { code: 'IPRS_MATCH', label: 'eCitizen IPRS match', hint: 'Matched against the national identity register' },
]

const RESULTS = [
  { code: 'PASSED', label: 'Passed', hint: 'The check confirmed the identity' },
  { code: 'FAILED', label: 'Failed', hint: 'The check contradicted the identity' },
  { code: 'INCONCLUSIVE', label: 'Inconclusive', hint: 'The check did not settle it either way' },
]

const RESULT_TONE: Record<string, string> = {
  PASSED: 'text-emerald-700 dark:text-emerald-400',
  FAILED: 'text-red-700 dark:text-red-400',
  INCONCLUSIVE: 'text-amber-700 dark:text-amber-400',
}

export function VerificationSection({
  voterId, currentStatus, onRecorded,
}: {
  voterId: string
  currentStatus?: string | null
  onRecorded?: () => void
}) {
  const canVerify = useCan('voter:verify')
  const [open, setOpen] = useState(false)
  const trail = useLoad(() => voterVerifications(voterId), [voterId])
  const rows = trail.data ?? []

  return (
    <DrawerSection
      title="Verification"
      right={
        canVerify ? (
          <button type="button" className="erp-btn" onClick={() => setOpen(true)}>
            <BadgeCheck className="h-3.5 w-3.5" /> Record
          </button>
        ) : undefined
      }
    >
      <DrawerField
        label="Status"
        value={currentStatus ? (VERIFICATION_LABEL[currentStatus] ?? currentStatus) : 'Unverified'}
      />

      <ErrorStrip message={trail.error} />

      {rows.length === 0 ? (
        <p className="px-4 py-3 text-erp-sm text-muted-foreground">
          {trail.loading
            ? 'Loading the trail…'
            : 'No verification has been recorded. The status above is the default, not a finding.'}
        </p>
      ) : (
        <ol className="px-4 py-2 space-y-2">
          {rows.map((v: VoterVerificationEntity) => (
            <li key={v.id} className="flex items-start gap-2 text-erp-sm">
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full shrink-0 mt-1.5',
                  v.result === 'PASSED' && 'bg-emerald-600',
                  v.result === 'FAILED' && 'bg-red-600',
                  v.result === 'INCONCLUSIVE' && 'bg-amber-500',
                )}
              />
              <div className="min-w-0">
                <div>
                  <span className="font-medium">
                    {METHODS.find((m) => m.code === v.method)?.label ?? v.method}
                  </span>{' '}
                  <span className={cn('text-erp-xs', RESULT_TONE[v.result])}>
                    {v.result?.toLowerCase()}
                  </span>
                </div>
                <div className="text-erp-xs text-muted-foreground">
                  {fmtDateTime(v.verifiedAt)}
                  {v.notes ? ` · ${v.notes}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {open && (
        <RecordVerificationModal
          voterId={voterId}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            void trail.refresh()
            onRecorded?.()
          }}
        />
      )}
    </DrawerSection>
  )
}

function RecordVerificationModal({
  voterId, onClose, onSaved,
}: {
  voterId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [method, setMethod] = useState('ID_SIGHTED')
  const [result, setResult] = useState('PASSED')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chosenMethod = METHODS.find((m) => m.code === method)
  const chosenResult = RESULTS.find((r) => r.code === result)

  return (
    <Modal
      open
      onClose={onClose}
      title="Record a verification"
      description="This appends to an append-only trail. The voter's status is derived from the most recent entry, so recording is how the status changes — there is no field to edit."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={saving}
            onClick={async () => {
              setSaving(true)
              setError(null)
              try {
                await addVerification(voterId, {
                  methodCode: method,
                  resultCode: result,
                  notes: notes.trim() || null,
                })
                onSaved()
              } catch (e) {
                setError(explain(e))
              } finally {
                setSaving(false)
              }
            }}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Record
          </button>
        </>
      }
    >
      <ErrorStrip message={error} />

      <label className="block">
        <span className="text-erp-xs text-muted-foreground">Method</span>
        <select className="erp-input w-full mt-1" value={method} onChange={(e) => setMethod(e.target.value)}>
          {METHODS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
        </select>
        {chosenMethod && <p className="text-erp-xs text-muted-foreground mt-1">{chosenMethod.hint}</p>}
      </label>

      <label className="block mt-3">
        <span className="text-erp-xs text-muted-foreground">Result</span>
        <select className="erp-input w-full mt-1" value={result} onChange={(e) => setResult(e.target.value)}>
          {RESULTS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
        {chosenResult && <p className="text-erp-xs text-muted-foreground mt-1">{chosenResult.hint}</p>}
      </label>

      {result === 'INCONCLUSIVE' && (
        <div className="mt-3">
          <Note tone="info">
            <span className="inline-flex items-start gap-1.5">
              <ShieldQuestion className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                An inconclusive check is recorded in the trail but leaves the current status
                standing — absence of evidence is not evidence of absence, so it will not undo a
                verification already established.
              </span>
            </span>
          </Note>
        </div>
      )}

      {result === 'FAILED' && (
        <div className="mt-3">
          <Note tone="bad">
            A failed check sets the voter to <strong>Failed</strong>, which is a visible state on
            the register. Use it when the check contradicted the identity, not when it merely did
            not complete.
          </Note>
        </div>
      )}

      <label className="block mt-3">
        <span className="text-erp-xs text-muted-foreground">Notes</span>
        <textarea
          className="erp-input w-full mt-1 min-h-[64px]"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What was seen, and by whom if not you."
        />
      </label>
    </Modal>
  )
}
